/**
 * Client workspace · Paid Media tab — wired against
 * `list-ad-accounts?client_id=`, `list-paid-media-campaigns?client_id=`,
 * `get-paid-media-campaign`, `list-paid-media-ad-sets`,
 * `list-paid-media-ads`.
 *
 * Per `docs/frontend/035-frontend-app-shell-plan.md §6.5`. Per task 038
 * spec: organize by platform tabs (Meta / Google / TikTok / Snapchat).
 * Mutation surfaces (record-media-buyer-action, review) are deferred —
 * action buttons are `.btn-coming-soon` only.
 *
 * Note: `paid_campaigns.platform` is flattened from `ad_accounts.platform`.
 * When the actor lacks `paid_media.account.read`, the embed resolves to
 * `null` (Task 032 §7.1 documented gap). The UI surfaces "—" for those
 * rows rather than crashing.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isApiError } from '../../api/client';
import {
  listAdAccounts,
  listClientPaidCampaigns,
  listPaidMediaAdSets,
  listPaidMediaAds,
  listPaidMediaCampaigns,
} from '../../api/endpoints';
import type {
  AdAccountRow,
  AdRow,
  AdSetRow,
  PaidCampaignRow,
  PaidMediaEntityStatus,
  PaidMediaPlatform,
} from '../../api/types';
import { CampaignMetricsPanel } from '../../components/paid-media/CampaignMetricsPanel';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../../components/states';
import { useMeContext } from '../../context/MeContextProvider';
import {
  PaidMediaConnectMetaAdsCard,
  hasOnlyClientCategoryRoles,
} from './PaidMediaConnectMetaAdsCard';
// Task 190 (IC-6) — Snapchat connect card. Behind its own
// env.ic6SnapchatOauthUiEnabled flag; default OFF.
import { PaidMediaConnectSnapchatAdsCard } from './PaidMediaConnectSnapchatAdsCard';
// Task 204 (IC-8) — TikTok connect card. Behind its own
// env.ic8TikTokOauthUiEnabled flag; default OFF.
import { PaidMediaConnectTikTokAdsCard } from './PaidMediaConnectTikTokAdsCard';
import {
  PaidMediaSyncMetaCampaignsButton,
  type PaidMediaSyncSummary,
} from './PaidMediaSyncMetaCampaignsButton';
// IC-7.4 (Task 195) — Snapchat manual-sync trigger (dedicated twin of the Meta
// button; internally gated by a syncable snapchat_ads account).
import {
  PaidMediaSyncSnapchatCampaignsButton,
  type PaidMediaSnapchatSyncSummary,
} from './PaidMediaSyncSnapchatCampaignsButton';

// Task 148 (IC-5 MVP — Codex Step A round-15 P1): this page is reached
// by BOTH client-portal users AND agency staff drilling into a client
// workspace (ClientWorkspaceLayout serves agency actors too). The read
// path branches by actor category:
//   - CLIENT-only actors → `list-client-paid-campaigns` (the new IC-5
//     aggregate EF; service-role internally, gated on client-category +
//     assignment). Campaign LIST only — the agency-RLS ad-set / ad /
//     metrics detail EFs would 404 under a client JWT, so the client
//     detail surface is a documented later increment.
//   - AGENCY / mixed / sysadmin actors → the existing
//     `list-paid-media-campaigns` (agency-RLS) + the full campaign
//     detail hierarchy, UNCHANGED from before IC-5.
// `hasOnlyClientCategoryRoles(me)` is the exact derivation the IC-5
// client EF gate uses server-side, so the FE branch and the EF gate
// agree.

type ListState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly accounts: ReadonlyArray<AdAccountRow>;
      readonly campaigns: ReadonlyArray<PaidCampaignRow>;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

type DetailState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly campaign: PaidCampaignRow;
      readonly adSets: ReadonlyArray<AdSetRow>;
      readonly selectedAdSetId: string | null;
      readonly ads: ReadonlyArray<AdRow>;
      readonly adsLoading: boolean;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

const PLATFORM_TABS: ReadonlyArray<{
  readonly value: PaidMediaPlatform | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'كل المنصات' },
  { value: 'meta_ads', label: 'Meta' },
  { value: 'google_ads', label: 'Google' },
  { value: 'tiktok_ads', label: 'TikTok' },
  { value: 'snapchat_ads', label: 'Snapchat' },
];

const STATUS_FILTERS: ReadonlyArray<{
  readonly value: PaidMediaEntityStatus | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'كل الحالات' },
  { value: 'active', label: 'نشطة' },
  { value: 'paused', label: 'متوقفة' },
  { value: 'completed', label: 'منتهية' },
  { value: 'archived', label: 'مؤرشفة' },
];

export function PaidMediaPage() {
  const { clientId } = useParams();
  const { me } = useMeContext();
  // CLIENT-only actor → list-only client EF; agency/mixed/sysadmin →
  // existing agency-RLS endpoint + full detail (Codex round-15 P1).
  const clientView = me ? hasOnlyClientCategoryRoles(me) : false;
  const [platform, setPlatform] = useState<PaidMediaPlatform | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<PaidMediaEntityStatus | 'all'>('all');
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);
  // Task 153 — last manual Meta sync summary. Held at the PAGE level (not
  // in the sync button) so it survives the reload that the sync triggers:
  // a refresh flips `list` to `loading`, unmounting the button's subtree,
  // so a banner rendered there would vanish (Codex Step A P2). Tagged with
  // its `clientId` and rendered only when it matches the current route, so
  // navigating to a different client workspace (React Router reuses this
  // component) never shows the previous client's counts (Codex Step B P2).
  const [syncSummary, setSyncSummary] = useState<
    (PaidMediaSyncSummary & { readonly clientId: string }) | null
  >(null);
  // IC-7.4 — a SEPARATE Snapchat summary banner (ad_squads, no creatives), kept
  // parallel to the Meta one so the Meta banner/state stay untouched.
  const [snapchatSyncSummary, setSnapchatSyncSummary] = useState<
    (PaidMediaSnapchatSyncSummary & { readonly clientId: string }) | null
  >(null);

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setList({ status: 'loading' });
    // Task 106 (T103-C): these list EFs are now keyset-paginated
    // (default 100 rows/page). This page consumes the FIRST PAGE ONLY;
    // the cursor-walk / "load more" UI is deferred to the T103-C-fe
    // follow-up. The api client already accepts `limit` / `cursor`.
    Promise.all([
      listAdAccounts({
        clientId,
        platform: platform === 'all' ? undefined : platform,
        signal: ctrl.signal,
      }),
      // Actor-branched campaigns read (Codex round-15 P1).
      clientView
        ? listClientPaidCampaigns({
            clientId,
            status: statusFilter === 'all' ? undefined : statusFilter,
            signal: ctrl.signal,
          })
        : listPaidMediaCampaigns({
            clientId,
            platform: platform === 'all' ? undefined : platform,
            status: statusFilter === 'all' ? undefined : statusFilter,
            signal: ctrl.signal,
          }),
    ])
      .then(([accountsData, campaignsData]) => {
        if (ctrl.signal.aborted) return;
        setList({
          status: 'ready',
          accounts: accountsData.accounts,
          campaigns: campaignsData.campaigns,
        });
        // Client view is list-only — never auto-select a campaign (no
        // detail panel for clients in the MVP). Agency view keeps the
        // original auto-select-first-campaign behavior.
        if (!clientView && campaignsData.campaigns.length > 0 && campaignsData.campaigns[0]) {
          setSelectedCampaignId((prev) =>
            prev && campaignsData.campaigns.some((c) => c.id === prev)
              ? prev
              : campaignsData.campaigns[0]!.id,
          );
        } else {
          setSelectedCampaignId(null);
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setList({ status: 'not-found' });
          return;
        }
        setList({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, platform, statusFilter, reloadKey, clientView]);

  useEffect(() => {
    // For the client view `selectedCampaignId` is never set, so this
    // effect short-circuits to idle and never calls the agency-RLS
    // ad-set / ad detail EFs (which would 404 under a client JWT).
    if (!selectedCampaignId || list.status !== 'ready') {
      setDetail({ status: 'idle' });
      return;
    }
    const campaign = list.campaigns.find((c) => c.id === selectedCampaignId);
    if (!campaign) {
      setDetail({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setDetail({ status: 'loading' });
    // Task 106 (T103-C): list-paid-media-ad-sets / -ads are keyset-
    // paginated; the detail panel shows the first page only (T103-C-fe).
    listPaidMediaAdSets(campaign.id, { signal: ctrl.signal })
      .then((adSetsData) => {
        if (ctrl.signal.aborted) return;
        const firstAdSet = adSetsData.ad_sets[0];
        setDetail({
          status: 'ready',
          campaign,
          adSets: adSetsData.ad_sets,
          selectedAdSetId: firstAdSet ? firstAdSet.id : null,
          ads: [],
          adsLoading: !!firstAdSet,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setDetail({ status: 'not-found' });
          return;
        }
        setDetail({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [selectedCampaignId, list]);

  // Effect dep is the primitive `activeAdSetId` (not the whole `detail` object)
  // so setDetail calls inside the effect don't re-fire it. Codex round-1 P2.
  const activeAdSetId = detail.status === 'ready' ? detail.selectedAdSetId : null;
  useEffect(() => {
    if (!activeAdSetId) return;
    const adSetId = activeAdSetId;
    const ctrl = new AbortController();
    setDetail((prev) =>
      prev.status === 'ready' && prev.selectedAdSetId === adSetId && !prev.adsLoading
        ? { ...prev, adsLoading: true }
        : prev,
    );
    listPaidMediaAds(adSetId, { signal: ctrl.signal })
      .then((adsData) => {
        if (ctrl.signal.aborted) return;
        setDetail((prev) =>
          prev.status === 'ready' && prev.selectedAdSetId === adSetId
            ? { ...prev, ads: adsData.ads, adsLoading: false }
            : prev,
        );
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setDetail((prev) =>
          prev.status === 'ready' && prev.selectedAdSetId === adSetId
            ? { ...prev, ads: [], adsLoading: false }
            : prev,
        );
        // Surface as inline tiny note rather than blowing up the whole panel.
        console.error('list-paid-media-ads failed', err);
      });
    return () => ctrl.abort();
  }, [activeAdSetId]);

  const accountsByPlatform = useMemo(() => {
    if (list.status !== 'ready') return new Map<PaidMediaPlatform, AdAccountRow[]>();
    const map = new Map<PaidMediaPlatform, AdAccountRow[]>();
    for (const account of list.accounts) {
      const bucket = map.get(account.platform);
      if (bucket) bucket.push(account);
      else map.set(account.platform, [account]);
    }
    return map;
  }, [list]);

  // Client view only: scope the campaign list to the active platform
  // tab (Codex round-8 P2). The client EF returns Meta-only rows, so a
  // non-Meta tab filters them out rather than showing Meta campaigns
  // under, e.g., the Google tab. The agency view keeps its own
  // server-side platform filtering via listPaidMediaCampaigns.
  const clientVisibleCampaigns = useMemo(() => {
    if (list.status !== 'ready') return [] as ReadonlyArray<PaidCampaignRow>;
    if (platform === 'all') return list.campaigns;
    return list.campaigns.filter((c) => c.platform === platform);
  }, [list, platform]);

  if (!clientId) return null;

  return (
    <div className="stack-md">
      {/* Task 141 — IC-3 Meta Ads OAuth connect CTA. Internal gating
          renders nothing for non-client-only or unassigned users; the
          IC-3 EF gate is the load-bearing authority either way.
          Task 145 (IC-4) — when an active Meta ad_account already
          exists for this workspace AND it's linked to an
          integration_connection, the card renders a quiet
          connected-state badge INSTEAD of the CTA.
          Task 191 — gate by the active platform tab: the Meta card shows
          on "all" and the Meta tab only (never on the Snapchat tab). The
          card's own connected-detection still keys off platform='meta_ads'
          + active integration_connection_status. */}
      {platform === 'all' || platform === 'meta_ads' ? (
        <PaidMediaConnectMetaAdsCard
          clientId={clientId}
          accounts={list.status === 'ready' ? list.accounts : []}
        />
      ) : null}

      {/* Task 190 (IC-6) — Snapchat connect surface. Same strict client-
          only + active-assignment gating as the Meta card, behind its own
          env.ic6SnapchatOauthUiEnabled flag (default OFF). Renders null
          when the flag is off or the gate fails.
          Task 191 — gate by the active platform tab: shows on "all" and
          the Snapchat tab only (never on the Meta tab). Connected-detection
          keys off platform='snapchat_ads' + active integration_connection_status. */}
      {platform === 'all' || platform === 'snapchat_ads' ? (
        <PaidMediaConnectSnapchatAdsCard
          clientId={clientId}
          accounts={list.status === 'ready' ? list.accounts : []}
        />
      ) : null}

      {/* Task 204 (IC-8) — TikTok connect surface. Same strict client-
          only + active-assignment gating as the Meta/Snapchat cards, behind
          its own env.ic8TikTokOauthUiEnabled flag (default OFF). Renders
          null when the flag is off or the gate fails.
          Gate by the active platform tab: shows on "all" and the TikTok tab
          only. Connected-detection keys off platform='tiktok_ads' + active
          integration_connection_status. */}
      {platform === 'all' || platform === 'tiktok_ads' ? (
        <PaidMediaConnectTikTokAdsCard
          clientId={clientId}
          accounts={list.status === 'ready' ? list.accounts : []}
        />
      ) : null}

      {/* Task 153 — durable manual-sync summary. Rendered here (top-level,
          NOT inside the reload-gated `list.status === 'ready'` subtree) so
          it survives the refresh the sync triggers (Codex Step A P2).
          Guarded on `clientId` match so a summary captured on another
          client workspace never shows here (Codex Step B P2). */}
      {syncSummary && syncSummary.clientId === clientId ? (
        <div
          className="banner banner-success"
          role="status"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <div>
            <strong>
              {syncSummary.partial
                ? 'تمت المزامنة جزئيًا — قد تكون بعض البيانات غير مكتملة'
                : 'تمت مزامنة بيانات Meta'}
            </strong>
            {/* Task 155 — per-entity counts (inserted/updated). The
                "أُضيفت/حُدِّثت" prefix is the legend for each pair. */}
            <div className="tiny" style={{ marginTop: 4, color: 'var(--muted)' }}>
              {`أُضيفت/حُدِّثت — الحملات ${syncSummary.campaigns.inserted}/${syncSummary.campaigns.updated}`}
              {` · المجموعات الإعلانية ${syncSummary.adSets.inserted}/${syncSummary.adSets.updated}`}
              {` · الإعلانات ${syncSummary.ads.inserted}/${syncSummary.ads.updated}`}
              {` · التصاميم ${syncSummary.creatives.inserted}/${syncSummary.creatives.updated}`}
              {` · الأداء اليومي ${syncSummary.dailyInsights.inserted}/${syncSummary.dailyInsights.updated}`}
            </div>
          </div>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setSyncSummary(null)}
            style={{ flexShrink: 0 }}
          >
            إخفاء
          </button>
        </div>
      ) : null}

      {/* IC-7.4 (Task 195) — durable Snapchat manual-sync summary. Parallel to
          the Meta banner above; Snapchat reports ad_squads (no creatives). */}
      {snapchatSyncSummary && snapchatSyncSummary.clientId === clientId ? (
        <div
          className="banner banner-success"
          role="status"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <div>
            <strong>
              {snapchatSyncSummary.partial
                ? 'تمت المزامنة جزئيًا — قد تكون بعض البيانات غير مكتملة'
                : 'تمت مزامنة بيانات Snapchat'}
            </strong>
            <div className="tiny" style={{ marginTop: 4, color: 'var(--muted)' }}>
              {`أُضيفت/حُدِّثت — الحملات ${snapchatSyncSummary.campaigns.inserted}/${snapchatSyncSummary.campaigns.updated}`}
              {` · المجموعات الإعلانية ${snapchatSyncSummary.adSquads.inserted}/${snapchatSyncSummary.adSquads.updated}`}
              {` · الإعلانات ${snapchatSyncSummary.ads.inserted}/${snapchatSyncSummary.ads.updated}`}
              {` · الأداء اليومي ${snapchatSyncSummary.dailyInsights.inserted}/${snapchatSyncSummary.dailyInsights.updated}`}
            </div>
          </div>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setSnapchatSyncSummary(null)}
            style={{ flexShrink: 0 }}
          >
            إخفاء
          </button>
        </div>
      ) : null}

      <div className="tabs" role="tablist">
        {PLATFORM_TABS.map((p) => (
          <button
            key={p.value}
            type="button"
            role="tab"
            className={p.value === platform ? 'tab active' : 'tab'}
            onClick={() => setPlatform(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="toolbar-row">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={statusFilter === f.value ? 'chip on' : 'chip'}
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {list.status === 'loading' ? <LoadingState title="جاري تحميل الإعلانات…" /> : null}
      {list.status === 'not-found' ? (
        <NotFoundState
          title="لا يمكن الوصول إلى الإعلانات لهذا العميل"
          description="إما أن العميل غير موجود، أو لا تملك صلاحية فتح الإعلانات."
        />
      ) : null}
      {list.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل الإعلانات"
          error={list.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}

      {list.status === 'ready' ? (
        <>
          <AdAccountsBlock
            platform={platform}
            accounts={list.accounts}
            accountsByPlatform={accountsByPlatform}
          />
          {/* Task 153 — agency manual Meta campaign-sync trigger. Internal
              gating renders nothing for client-only / unpermissioned
              actors. On a succeeded/partial sync it records the summary
              (rendered as a durable banner near the top, OUTSIDE this
              reload-gated subtree) and bumps reloadKey so the campaign
              list re-fetches. */}
          <PaidMediaSyncMetaCampaignsButton
            clientId={clientId}
            accounts={list.accounts}
            onSyncSucceeded={(summary) => {
              setSyncSummary({ ...summary, clientId });
              setReloadKey((n) => n + 1);
            }}
          />
          {/* IC-7.4 (Task 195) — agency manual Snapchat campaign-sync trigger.
              Dedicated twin; internally gated by a syncable snapchat_ads
              account (renders nothing otherwise). Records a SEPARATE Snapchat
              summary banner + bumps reloadKey on a succeeded/partial sync. */}
          <PaidMediaSyncSnapchatCampaignsButton
            clientId={clientId}
            accounts={list.accounts}
            onSyncSucceeded={(summary) => {
              setSnapchatSyncSummary({ ...summary, clientId });
              setReloadKey((n) => n + 1);
            }}
          />
          {clientView ? (
            // CLIENT view (Codex round-15 P1): list-only, no detail
            // panel. The ad-set / ad / metrics detail EFs are
            // agency-RLS and would 404 under a client JWT; a
            // client-side detail surface is a documented later
            // increment (Task 148 §9 follow-up).
            clientVisibleCampaigns.length === 0 ? (
              <EmptyState title="لا توجد حملات مطابقة" description="جرّب فلتر آخر أو منصة أخرى." />
            ) : (
              <div className="card" style={{ padding: 22 }}>
                <div className="sec-title">
                  <h2>الحملات</h2>
                  <span className="meta">{clientVisibleCampaigns.length}</span>
                </div>
                <ul className="row-list">
                  {clientVisibleCampaigns.map((c) => (
                    <li key={c.id}>
                      <div className="row-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <strong style={{ fontSize: 13 }}>{c.name}</strong>
                          <span className={`pill pill-${paidStatusVariant(c.status)}`}>
                            {paidStatusLabel(c.status)}
                          </span>
                        </div>
                        <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                          {platformLabel(c.platform)} · {c.objective ?? 'بدون هدف'}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )
          ) : list.campaigns.length === 0 ? (
            <EmptyState title="لا توجد حملات مطابقة" description="جرّب فلتر آخر أو منصة أخرى." />
          ) : (
            <div className="workspace-split">
              <aside className="workspace-list">
                <div className="sec-title">
                  <h2>الحملات</h2>
                  <span className="meta">{list.campaigns.length}</span>
                </div>
                <ul className="row-list">
                  {list.campaigns.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={
                          c.id === selectedCampaignId ? 'row-card row-card-active' : 'row-card'
                        }
                        onClick={() => setSelectedCampaignId(c.id)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <strong style={{ fontSize: 13 }}>{c.name}</strong>
                          <span className={`pill pill-${paidStatusVariant(c.status)}`}>
                            {paidStatusLabel(c.status)}
                          </span>
                        </div>
                        <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                          {platformLabel(c.platform)} · {c.objective ?? 'بدون هدف'}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </aside>
              <section className="workspace-detail">
                <CampaignDetail
                  state={detail}
                  onSelectAdSet={(adSetId) => {
                    // Re-clicking the active ad set is a no-op so the loading
                    // state isn't stranded — `activeAdSetId` wouldn't change,
                    // so the fetch effect wouldn't re-fire (Codex round-2 P2).
                    setDetail((prev) =>
                      prev.status === 'ready' && prev.selectedAdSetId !== adSetId
                        ? {
                            ...prev,
                            selectedAdSetId: adSetId,
                            ads: [],
                            adsLoading: true,
                          }
                        : prev,
                    );
                  }}
                />
              </section>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function AdAccountsBlock({
  platform,
  accounts,
  accountsByPlatform,
}: {
  readonly platform: PaidMediaPlatform | 'all';
  readonly accounts: ReadonlyArray<AdAccountRow>;
  readonly accountsByPlatform: Map<PaidMediaPlatform, AdAccountRow[]>;
}) {
  if (accounts.length === 0) {
    return (
      <div className="card" style={{ padding: 18 }}>
        <div className="sec-title">
          <h2>حسابات الإعلانات</h2>
        </div>
        <EmptyState
          title="لا توجد حسابات مرتبطة"
          description="اربط حساب إعلانات Meta أو Google لتبدأ بتتبع الحملات."
        />
      </div>
    );
  }

  if (platform === 'all') {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
          gap: 14,
        }}
      >
        {Array.from(accountsByPlatform.entries()).map(([p, list]) => (
          <div key={p} className="card" style={{ padding: 18 }}>
            <div className="sec-title">
              <h2>{platformLabel(p)}</h2>
              <span className="meta">{list.length} حساب</span>
            </div>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {list.map((a) => (
                <li key={a.id} className="tiny">
                  <strong style={{ color: 'var(--ink)', fontSize: 12 }}>{a.account_name}</strong>
                  <span style={{ color: 'var(--muted)', marginInlineStart: 6 }}>
                    {a.account_external_id}
                  </span>
                  <span
                    className={`pill pill-${adAccountStatusVariant(a.status)}`}
                    style={{ marginInlineStart: 8, fontSize: 10 }}
                  >
                    {adAccountStatusLabel(a.status)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 18 }}>
      <div className="sec-title">
        <h2>{platformLabel(platform)}</h2>
        <span className="meta">{accounts.length} حساب</span>
      </div>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {accounts.map((a) => (
          <li
            key={a.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 12px',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-sm)',
            }}
          >
            <div>
              <strong style={{ fontSize: 13 }}>{a.account_name}</strong>
              <div className="tiny" style={{ color: 'var(--muted)' }}>
                {a.account_external_id}
                {a.currency_code ? ` · ${a.currency_code}` : ''}
              </div>
            </div>
            <span className={`pill pill-${adAccountStatusVariant(a.status)}`}>
              {adAccountStatusLabel(a.status)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CampaignDetail({
  state,
  onSelectAdSet,
}: {
  readonly state: DetailState;
  readonly onSelectAdSet: (adSetId: string) => void;
}) {
  if (state.status === 'idle')
    return (
      <EmptyState
        title="اختر حملة لعرض التفاصيل"
        description="اضغط أي حملة من القائمة على اليمين."
      />
    );
  if (state.status === 'loading') return <LoadingState title="جاري تحميل تفاصيل الحملة…" />;
  if (state.status === 'not-found')
    return (
      <NotFoundState
        title="الحملة غير متاحة"
        description="ربما تم أرشفتها أو لا تملك صلاحية فتحها."
      />
    );
  if (state.status === 'error')
    return <ErrorState title="تعذر تحميل تفاصيل الحملة" error={state.error} />;

  const { campaign, adSets, selectedAdSetId, ads, adsLoading } = state;
  return (
    <div className="stack-md">
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>{campaign.name}</h2>
          <span className="meta">
            {platformLabel(campaign.platform)} · {paidStatusLabel(campaign.status)}
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 14,
          }}
        >
          <Field label="الهدف" value={campaign.objective} />
          <Field label="نوع الشراء" value={campaign.buying_type} />
          <Field
            label="بدأت"
            value={
              campaign.started_at ? new Date(campaign.started_at).toLocaleDateString('ar-SA') : null
            }
          />
          <Field
            label="انتهت"
            value={
              campaign.ended_at ? new Date(campaign.ended_at).toLocaleDateString('ar-SA') : null
            }
          />
          <Field
            label="آخر مزامنة"
            value={
              campaign.last_synced_at
                ? new Date(campaign.last_synced_at).toLocaleString('ar-SA')
                : null
            }
          />
          <Field label="معرف المنصة" value={campaign.platform_campaign_id} mono />
        </div>
      </div>

      <CampaignMetricsPanel paidCampaignId={campaign.id} adSets={adSets} />

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>المجموعات الإعلانية (Ad Sets)</h2>
          <span className="meta">{adSets.length}</span>
        </div>
        {adSets.length === 0 ? (
          <EmptyState
            title="لا توجد مجموعات إعلانية"
            description="ستظهر هنا بمجرد مزامنة الحملة من المنصة."
          />
        ) : (
          <ul className="row-list" style={{ marginBottom: 14 }}>
            {adSets.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={s.id === selectedAdSetId ? 'row-card row-card-active' : 'row-card'}
                  onClick={() => onSelectAdSet(s.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <strong style={{ fontSize: 13 }}>{s.name}</strong>
                    <span className={`pill pill-${paidStatusVariant(s.status)}`}>
                      {paidStatusLabel(s.status)}
                    </span>
                  </div>
                  <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                    {s.optimization_goal ?? 'بدون هدف تحسين'} ·{' '}
                    {s.billing_event ?? 'بدون billing event'}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="sec-title" style={{ marginTop: 8 }}>
          <h2>الإعلانات</h2>
          <span className="meta">{adsLoading ? 'جاري التحميل…' : `${ads.length}`}</span>
        </div>
        {adsLoading ? (
          <LoadingState title="جاري تحميل الإعلانات…" variant="inline" />
        ) : ads.length === 0 ? (
          <EmptyState
            title="لا توجد إعلانات في هذه المجموعة"
            description="ستظهر هنا بمجرد مزامنة المجموعة."
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>اسم الإعلان</th>
                <th>الحالة</th>
                <th>معرف المنصة</th>
                <th>آخر مزامنة</th>
              </tr>
            </thead>
            <tbody>
              {ads.map((a) => (
                <tr key={a.id}>
                  <td>
                    <span className="name">{a.name}</span>
                    {a.creative_external_id ? (
                      <div className="sub">{a.creative_external_id}</div>
                    ) : null}
                  </td>
                  <td>
                    <span className={`pill pill-${paidStatusVariant(a.status)}`}>
                      {paidStatusLabel(a.status)}
                    </span>
                  </td>
                  <td className="num">{a.platform_ad_id}</td>
                  <td>
                    {a.last_synced_at ? new Date(a.last_synced_at).toLocaleString('ar-SA') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn-coming-soon" disabled>
          إيقاف الحملة (قريباً)
        </button>
        <button type="button" className="btn-coming-soon" disabled>
          تعديل الميزانية (قريباً)
        </button>
      </div>
    </div>
  );
}

function platformLabel(platform: PaidMediaPlatform | null): string {
  if (!platform) return '—';
  switch (platform) {
    case 'meta_ads':
      return 'Meta';
    case 'google_ads':
      return 'Google';
    case 'tiktok_ads':
      return 'TikTok';
    case 'snapchat_ads':
      return 'Snapchat';
    default:
      return platform;
  }
}

function paidStatusLabel(status: PaidMediaEntityStatus): string {
  switch (status) {
    case 'active':
      return 'نشطة';
    case 'paused':
      return 'متوقفة';
    case 'completed':
      return 'منتهية';
    case 'archived':
      return 'مؤرشفة';
    case 'unknown':
      return 'غير معروفة';
    default:
      return status;
  }
}

function paidStatusVariant(status: PaidMediaEntityStatus): string {
  switch (status) {
    case 'active':
      return 'active';
    case 'paused':
      return 'paused';
    case 'completed':
      return 'info';
    case 'archived':
      return 'archived';
    case 'unknown':
      return 'draft';
    default:
      return 'draft';
  }
}

function adAccountStatusLabel(status: AdAccountRow['status']): string {
  switch (status) {
    case 'active':
      return 'نشط';
    case 'reauthorization_required':
      return 'يحتاج إعادة ربط';
    case 'disabled':
      return 'معطل';
    case 'archived':
      return 'مؤرشف';
    default:
      return status;
  }
}

function adAccountStatusVariant(status: AdAccountRow['status']): string {
  switch (status) {
    case 'active':
      return 'active';
    case 'reauthorization_required':
      return 'review';
    case 'disabled':
      return 'error';
    case 'archived':
      return 'archived';
    default:
      return 'draft';
  }
}

function Field({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
  readonly mono?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body" style={mono ? { fontFamily: 'var(--font-mono)', fontSize: 11 } : {}}>
        {value && value.length > 0 ? value : '—'}
      </span>
    </div>
  );
}
